import { makeAutoObservable } from "mobx";
import { JobApi } from "../../apis/Job";
import { ResumeApi } from "../../apis/Resume";
import { ApplicationManageStatus, JobApplicant } from "../../models/JobApplicant";
import { Resume } from "../../models/Resume";
import {
    RemoteJob,
    SaveRemoteJobParam,
    createEmptyJobForm,
    isJobActive,
} from "../../models/RemoteJob";

export class JobManagementPageModel {
    jobs: RemoteJob[] = [];
    loading = false;
    saving = false;
    closingJobId: string | null = null;
    selectedStatus: "all" | "active" | "closed" = "all";
    showModal = false;
    isEditMode = false;
    editingJob: SaveRemoteJobParam = createEmptyJobForm();
    selectedJob: RemoteJob | null = null;
    jobDetailModalVisible = false;
    applicants: JobApplicant[] = [];
    loadingApplicants = false;
    selectedApplicant: JobApplicant | null = null;
    applicationDetailVisible = false;
    applicationDetailResume: Resume | null = null;
    loadingApplicationDetail = false;
    updatingApplicationStatus = false;

    constructor() {
        makeAutoObservable(this);
    }

    get displayJobs(): RemoteJob[] {
        if (this.selectedStatus === "active") {
            return this.jobs.filter(isJobActive);
        }
        if (this.selectedStatus === "closed") {
            return this.jobs.filter(job => !isJobActive(job));
        }
        return this.jobs;
    }

    setSelectedStatus = (status: "all" | "active" | "closed") => {
        this.selectedStatus = status;
    };

    loadJobs = async () => {
        this.loading = true;
        try {
            this.jobs = await JobApi.getJobs();
        } finally {
            this.loading = false;
        }
    };

    showJobDetail = async (job: RemoteJob) => {
        this.selectedJob = job;
        this.jobDetailModalVisible = true;
        await this.reloadApplicants();
    };

    reloadApplicants = async () => {
        if (!this.selectedJob) {
            return;
        }
        this.loadingApplicants = true;
        try {
            this.applicants = await JobApi.getApplicants(this.selectedJob.id);
        } finally {
            this.loadingApplicants = false;
        }
    };

    closeJobDetailModal = () => {
        this.jobDetailModalVisible = false;
        this.selectedJob = null;
        this.applicants = [];
        this.closeApplicationDetailModal();
    };

    showApplicationDetail = async (applicant: JobApplicant) => {
        if (!applicant.cvId || this.loadingApplicationDetail) {
            return;
        }
        this.selectedApplicant = applicant;
        this.applicationDetailVisible = true;
        this.loadingApplicationDetail = true;
        this.applicationDetailResume = null;
        try {
            this.applicationDetailResume = await ResumeApi.getResumeDetail(applicant.cvId);
        } finally {
            this.loadingApplicationDetail = false;
        }
    };

    closeApplicationDetailModal = () => {
        this.applicationDetailVisible = false;
        this.selectedApplicant = null;
        this.applicationDetailResume = null;
    };

    updateApplicationStatus = async (status: ApplicationManageStatus): Promise<boolean> => {
        const applicant = this.selectedApplicant;
        if (!applicant || this.updatingApplicationStatus) {
            return false;
        }
        this.updatingApplicationStatus = true;
        try {
            const success = await JobApi.updateApplicationStatus(applicant.applicationId, status);
            if (success) {
                await this.reloadApplicants();
                const updated = this.applicants.find(
                    item => item.applicationId === applicant.applicationId,
                );
                if (updated) {
                    this.selectedApplicant = updated;
                } else {
                    this.selectedApplicant = { ...applicant, status };
                }
            }
            return success;
        } finally {
            this.updatingApplicationStatus = false;
        }
    };

    showCreateModal = () => {
        this.isEditMode = false;
        this.editingJob = createEmptyJobForm();
        this.showModal = true;
    };

    showEditModal = (job: RemoteJob) => {
        this.isEditMode = true;
        this.editingJob = {
            id: job.id,
            title: job.title,
            description: job.description,
            type: job.type,
            salaryRangeStart: job.salaryRangeStart,
            salaryRangeEnd: job.salaryRangeEnd,
        };
        this.showModal = true;
    };

    closeModal = () => {
        this.showModal = false;
        this.editingJob = createEmptyJobForm();
    };

    updateEditingField = <K extends keyof SaveRemoteJobParam>(key: K, value: SaveRemoteJobParam[K]) => {
        this.editingJob = {
            ...this.editingJob,
            [key]: value,
        };
    };

    saveJob = async (): Promise<boolean> => {
        if (this.saving) {
            return false;
        }
        this.saving = true;
        try {
            const result = this.isEditMode
                ? await JobApi.updateJob(this.editingJob)
                : await JobApi.createJob(this.editingJob);
            if (result) {
                await this.loadJobs();
                this.closeModal();
                return true;
            }
            return false;
        } finally {
            this.saving = false;
        }
    };

    closeJob = async (jobId: string): Promise<boolean> => {
        if (this.closingJobId) {
            return false;
        }
        this.closingJobId = jobId;
        try {
            const success = await JobApi.closeJob(jobId);
            if (success) {
                await this.loadJobs();
            }
            return success;
        } finally {
            this.closingJobId = null;
        }
    };
}
